import { useState, useEffect } from 'react';

interface DnssecSettings {
  enabled: boolean;
  algorithm: string;
  keyLength: number;
  autoRotation: boolean;
  rotationPeriod: number;
}

interface SecuritySettings {
  rateLimiting: boolean;
  ipWhitelist: boolean;
  apiKeyRotation: boolean;
  auditLogging: boolean;
  encryptionAtRest: boolean;
}

interface Zone {
  id: string;
  name: string;
  dnssecEnabled: boolean;
}

export const useSecurityData = () => {
  const [dnssecSettings, setDnssecSettings] = useState<DnssecSettings>({
    enabled: false,
    algorithm: 'ECDSA-P256-SHA256',
    keyLength: 256,
    autoRotation: true,
    rotationPeriod: 30,
  });

  const [securitySettings, setSecuritySettings] = useState<SecuritySettings>({
    rateLimiting: true,
    ipWhitelist: true,
    apiKeyRotation: true,
    auditLogging: true,
    encryptionAtRest: true,
  });

  const [zones, setZones] = useState<Zone[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchZones = async () => {
    try {
      const res = await fetch('/api/v1/dns/zones?include=dnssec');
      if (res.ok) {
        const data = await res.json();
        setZones(data?.zones || []);
      } else {
        setZones([]);
      }
    } catch (error) {
      console.error('Failed to fetch zones:', error);
      setZones([]);
    }
  };

  const fetchSecuritySettings = async () => {
    try {
      const response = await fetch('/api/settings/security');
      if (response.ok) {
        const data = await response.json();
        setSecuritySettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch security settings:', error);
    }
  };

  const fetchDnssecSettings = async () => {
    try {
      const response = await fetch('/api/settings/dnssec');
      if (response.ok) {
        const data = await response.json();
        setDnssecSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch DNSSEC settings:', error);
    }
  };

  const saveDnssecSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/settings/dnssec', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dnssecSettings),
      });

      if (!response.ok) {
        console.error('Failed to save DNSSEC settings');
      }
    } catch (error) {
      console.error('Failed to save DNSSEC settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSecuritySettings = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/settings/security', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(securitySettings),
      });

      if (!response.ok) {
        console.error('Failed to save security settings');
      }
    } catch (error) {
      console.error('Failed to save security settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDnssecToggle = async (zoneId: string) => {
    setLoading(true);
    try {
      const zone = zones.find((z) => z.id === zoneId);
      const response = await fetch(`/api/zones/${zoneId}/dnssec`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !zone?.dnssecEnabled }),
      });

      if (response.ok) {
        fetchZones();
      } else {
        console.error('Failed to toggle DNSSEC');
      }
    } catch (error) {
      console.error('Failed to toggle DNSSEC:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateApiKey = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/security/api-keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'Generated Key', permissions: ['read', 'write'] }),
      });

      if (!response.ok) {
        console.error('Failed to generate API key');
      }
    } catch (error) {
      console.error('Failed to generate API key:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchZones();
    fetchSecuritySettings();
    fetchDnssecSettings();
  }, []);

  return {
    dnssecSettings,
    setDnssecSettings,
    securitySettings,
    setSecuritySettings,
    zones,
    loading,
    saveDnssecSettings,
    saveSecuritySettings,
    handleDnssecToggle,
    generateApiKey,
  };
};